import type { InvoiceReturn } from "@/lib/api";

export type InvoiceReturnKindFilter = "all" | "invoice" | "status";
export type InvoiceReturnResultFilter = "all" | "ok" | "pending";

export interface InvoiceReturnFilters {
  search: string;
  kind: InvoiceReturnKindFilter;
  result: InvoiceReturnResultFilter;
}

export const INVOICE_RETURN_KIND_OPTIONS: { value: InvoiceReturnKindFilter; label: string }[] = [
  { value: "all", label: "Todos os tipos" },
  { value: "invoice", label: "Nota fiscal" },
  { value: "status", label: "Status da OV" },
];

export const INVOICE_RETURN_RESULT_OPTIONS: { value: InvoiceReturnResultFilter; label: string }[] = [
  { value: "all", label: "Todos os resultados" },
  { value: "ok", label: "Recebidos" },
  { value: "pending", label: "Com pendência" },
];

const STATUS_LABELS: Record<string, string> = {
  PreOrderPendenteERPDynamics: "Pré-pedido pendente",
  PreOrderAprovadoERPDynamics: "Pré-pedido aprovado",
  PreOrderConfirmadoERPDynamics: "Pré-pedido confirmado",
  PreOrderReprovadoERPDynamics: "Pré-pedido reprovado",
  OrdemEmAbertoERPDynamics: "Ordem em aberto",
  EntregueERPDynamics: "Entregue",
  FaturadoERPDynamics: "Faturado",
  CanceladoERPDynamics: "Cancelado",
};

/** Nome do tipo de purchase_orders_status em português; tipo desconhecido aparece sem o sufixo "ERPDynamics". */
export const invoiceReturnStatusLabel = (name: string) =>
  STATUS_LABELS[name] ?? name.replace(/ERPDynamics$/, "");

export const invoiceReturnKindLabel = (kind: string) => (kind === "invoice" ? "Nota fiscal" : "Status da OV");

/** Texto principal e secundário da coluna "Detalhe". */
export const invoiceReturnDetail = (item: InvoiceReturn): { title: string; subtitle: string } => {
  const salesIds = item.salesIds.length > 0 ? `OV ${item.salesIds.join(", ")}` : "";

  if (item.kind === "invoice") {
    const files = item.files.length > 0 ? item.files.map((file) => file.toUpperCase()).join(" · ") : "sem arquivo";
    return {
      title: `NF ${item.invoiceNumber ?? "—"}`,
      subtitle: [files, salesIds].filter(Boolean).join(" · "),
    };
  }

  return {
    title: item.statuses.length > 0 ? item.statuses.map(invoiceReturnStatusLabel).join(", ") : "—",
    subtitle: salesIds,
  };
};

const normalize = (value: string) => value.trim().toLowerCase();

/** Filtro feito no navegador (a API devolve só as últimas chamadas): busca, tipo e resultado. */
export const filterInvoiceReturns = (items: InvoiceReturn[], filters: InvoiceReturnFilters): InvoiceReturn[] => {
  const term = normalize(filters.search);

  return items.filter((item) => {
    if (filters.kind !== "all" && item.kind !== filters.kind) return false;
    if (filters.result === "ok" && !item.success) return false;
    if (filters.result === "pending" && item.success) return false;
    if (!term) return true;

    const haystack = [
      item.orderNumber,
      item.clientName,
      item.clientCode,
      item.preOrderId,
      item.preOrderReference,
      item.invoiceNumber,
      ...item.salesIds,
    ];
    return haystack.some((value) => value != null && normalize(value).includes(term));
  });
};

export const summarizeInvoiceReturns = (items: InvoiceReturn[]) => ({
  total: items.length,
  invoices: items.filter((item) => item.kind === "invoice").length,
  statuses: items.filter((item) => item.kind === "status").length,
  pending: items.filter((item) => !item.success).length,
});

const REASON_LABELS: Record<string, string> = {
  order_not_found: "Pedido não encontrado entre os enviados ao ERP",
  order_not_integrated: "Pedido ainda não aceito pelo ERP",
  invalid_payload: "Dados da chamada inválidos ou ilegíveis",
  invalid_file: "Arquivo inválido",
  invoice_not_identified: "Número da nota não identificado",
  file_too_large: "Arquivo acima do limite de tamanho",
  access_key_mismatch: "Chave de acesso do XML não confere",
  invoice_in_other_order: "Nota já ligada a outro pedido",
  unmapped_status: "Status não reconhecido",
  storage_unavailable: "Armazenamento das notas (S3) indisponível",
  internal_error: "Erro interno ao processar",
  feature_disabled: "Recebimento desligado no ambiente",
};

const REASON_HINTS: Record<string, string> = {
  order_not_found: "Confira o número do pré-pedido: se estiver errado, corrija e reprocesse.",
  order_not_integrated: "Reprocesse quando o pedido tiver sido aceito pelo ERP.",
  invalid_payload: "Corrija os campos (ou o JSON, se estiver ilegível) e reprocesse.",
  invalid_file: "O arquivo veio inválido do ERP: peça o reenvio. Descarte esta chamada depois.",
  invoice_not_identified: "O arquivo não traz o número da nota (PDF, ou XML sem nNF) e a OV não tem uma nota única: informe o número da nota na correção e reprocesse.",
  file_too_large: "O arquivo passa do limite: peça o reenvio ao ERP.",
  access_key_mismatch: "Confira a chave de acesso informada, corrija e reprocesse.",
  invoice_in_other_order: "O número da nota já pertence a outro pedido: confira o número ou o pedido antes de reprocessar.",
  unmapped_status: "Cadastre o status em purchase_orders_status_type (ou corrija o status aqui) e reprocesse.",
  storage_unavailable: "Reprocesse quando o S3 estiver disponível.",
  internal_error: "Reprocesse; se repetir, veja o log da API.",
  feature_disabled: "Reprocesse depois de ligar o recebimento no ambiente.",
};

export const invoiceReturnReasonLabel = (reason?: string | null) =>
  reason ? (REASON_LABELS[reason] ?? reason) : "—";

export const invoiceReturnReasonHint = (reason?: string | null) => (reason ? REASON_HINTS[reason] ?? null : null);

const STATE_LABELS: Record<string, string> = { pending: "Pendente", resolved: "Resolvida", discarded: "Descartada" };

export const invoiceReturnStateLabel = (status: string) => STATE_LABELS[status] ?? status;

/** Tamanho legível de um arquivo (KB ou MB). */
export const formatFileSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
